import { Info as InfoIcon } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { publicAsset } from '@utils/publicAsset';

import type { EducationSlideProps } from './types';

/**
 * Renders a single education / "how it works" slide: icon + title + description,
 * optional illustration, bullet list, and a highlighted callout. Pure presentation,
 * driven entirely by the slide config — reused by upsell education and onboarding.
 */
export const EducationSlide: React.FC<EducationSlideProps> = ({ slide }) => {
  const Icon = slide.icon;

  return (
    <Box sx={{ px: 3, py: 2.5 }}>
      {/* Icon + Title + Description */}
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', mb: 2 }}>
        <Box
          sx={{
            bgcolor: 'grey.100',
            color: 'primary.main',
            borderRadius: 3,
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 24 }} />
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            {slide.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {slide.description}
          </Typography>
        </Box>
      </Stack>

      {/* Optional illustration image */}
      {slide.illustrationSrc && (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            mb: 2,
            bgcolor: 'grey.50',
          }}
        >
          <img
            src={publicAsset(slide.illustrationSrc)}
            alt={slide.title}
            style={{ width: '100%', display: 'block' }}
          />
        </Box>
      )}

      {/* Bullet points */}
      {slide.bullets && slide.bullets.length > 0 && (
        <Stack spacing={1.5} sx={{ mb: slide.callout ? 2 : 0 }}>
          {slide.bullets.map((bullet, idx) => (
            <Stack key={idx} direction="row" spacing={1.5} sx={{ alignItems: 'baseline' }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  flexShrink: 0,
                  // Pull dot down from baseline to vertically center with first text line
                  position: 'relative',
                  top: -1,
                }}
              />
              <Typography variant="body2">{bullet}</Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {/* Optional callout/tip box */}
      {slide.callout && (
        <Box
          sx={{
            bgcolor: '#FFF8E1',
            borderRadius: 2,
            p: 2,
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
          }}
        >
          <InfoIcon sx={{ color: 'warning.main', fontSize: 20, mt: 0.25, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            <Typography component="span" variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>
              {slide.callout.label}
            </Typography>{' '}
            {slide.callout.text}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
